/**
 * FormPanel — sidebar de cadastro/edicao de parceiro (Representante).
 *
 * Componente autossuficiente: gerencia o proprio estado do formulario, faz
 * create/update via services e devolve o parceiro salvo por `onSaved`. Reusado
 * pela lista (`index.jsx`) e pelo cockpit (`Detalhe.jsx`) — fonte unica do form
 * (DRY), evita as ~90 linhas de campos duplicadas.
 *
 * Props:
 *   open      — boolean
 *   parceiro  — objeto Representante (edicao) ou null (novo)
 *   onClose   — fecha sem salvar
 *   onSaved   — (repSalvo) => void; chamado apos criar/atualizar com sucesso
 */
import React, { useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { atualizarRepresentante, criarRepresentante } from "../../../services/comercial";

import { CICLOS, REDES_SOCIAIS, SECAO_STYLE, SEG_TIPOS, VAZIO } from "./shared";

function fromParceiro(p) {
  if (!p) return { ...VAZIO };
  return {
    nome: p.nome || "",
    documento: p.documento || "",
    email: p.email || "",
    telefone: p.telefone || "",
    tipo: p.tipo || "ARQUITETO",
    comissao_percentual: String(p.comissao_percentual ?? "0"),
    responsavel: p.responsavel || "",
    cidade: p.cidade || "",
    uf: p.uf || "",
    ...Object.fromEntries(REDES_SOCIAIS.map((r) => [r.key, p[r.key] || ""])),
    banco: p.banco || "",
    chave_pix: p.chave_pix || "",
    ciclo_pagamento: p.ciclo_pagamento || "",
    observacoes: p.observacoes || "",
    ativo: p.ativo !== false,
  };
}

export default function ParceiroFormPanel({ open, parceiro, onClose, onSaved }) {
  const { request } = useAuth();
  const toast = useToast();

  const [dados, setDados] = useState(VAZIO);
  const [erroForm, setErroForm] = useState(null);

  const editandoId = parceiro?.id || null;

  // Sincroniza o form quando abre / troca de parceiro.
  useEffect(() => {
    if (open) {
      setDados(fromParceiro(parceiro));
      setErroForm(null);
    }
  }, [open, parceiro]);

  const set = (campo) => (e) => setDados((d) => ({ ...d, [campo]: e.target.value }));

  const salvar = async () => {
    setErroForm(null);
    if (!dados.nome.trim()) {
      setErroForm("Nome é obrigatório.");
      return;
    }
    try {
      const salvo = editandoId
        ? await atualizarRepresentante(request, editandoId, dados)
        : await criarRepresentante(request, dados);
      toast.success(`Parceiro "${dados.nome}" ${editandoId ? "atualizado" : "criado"}.`);
      onSaved?.(salvo);
    } catch (err) {
      setErroForm(err?.message || "Erro ao salvar.");
      throw err; // mantem o spinner do AsyncButton em estado de erro
    }
  };

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={editandoId ? "Editar parceiro" : "Novo parceiro"}
      subtitle="Cadastre quem indica clientes: arquiteto, designer, revendedor, broker ou outro"
      width="min(95vw, 520px)"
      footer={
        <div className="flex gap-2" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancelar
          </button>
          <AsyncButton variant="primary" onClick={salvar} silentError>
            Salvar parceiro
          </AsyncButton>
        </div>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          salvar();
        }}
      >
        <div style={SECAO_STYLE}>TIPO DE PARCEIRO</div>
        <div className="seg five">
          {SEG_TIPOS.map(([v, l]) => (
            <button
              key={v}
              type="button"
              className={dados.tipo === v ? "is-on" : ""}
              aria-pressed={dados.tipo === v}
              onClick={() => setDados((d) => ({ ...d, tipo: v }))}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="input-group">
          <label className="input-label">Nome / Razão social *</label>
          <input className="input" value={dados.nome} onChange={set("nome")} autoFocus />
        </div>

        <div className="row-2col" style={{ gap: 12 }}>
          <div className="input-group">
            <label className="input-label">CNPJ / CPF</label>
            <input className="input mono" value={dados.documento} onChange={set("documento")} placeholder="00.000.000/0000-00" />
          </div>
          <div className="input-group">
            <label className="input-label">RT comissão</label>
            <div className="input-prefixed">
              <input
                type="number" step="0.01" min="0" max="100"
                value={dados.comissao_percentual}
                onChange={set("comissao_percentual")}
              />
              <span className="mono">%</span>
            </div>
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Responsável (contato)</label>
          <input className="input" value={dados.responsavel} onChange={set("responsavel")} />
        </div>
        <div className="input-group">
          <label className="input-label">Email</label>
          <input className="input" type="email" value={dados.email} onChange={set("email")} />
        </div>
        <div className="row-2col" style={{ gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Telefone / WhatsApp</label>
            <input className="input" value={dados.telefone} onChange={set("telefone")} placeholder="(11) 99999-9999" />
          </div>
          <div className="input-group">
            <label className="input-label">Cidade · UF</label>
            <div className="flex gap-2">
              <input className="input" value={dados.cidade} onChange={set("cidade")} placeholder="Cidade" />
              <input
                className="input" style={{ maxWidth: 64 }} maxLength={2}
                value={dados.uf}
                onChange={(e) => setDados((d) => ({ ...d, uf: e.target.value.toUpperCase() }))}
                placeholder="UF"
              />
            </div>
          </div>
        </div>

        <div style={{ ...SECAO_STYLE, marginTop: 6 }}>REDES SOCIAIS</div>
        <div className="row-2col" style={{ gap: 12 }}>
          {REDES_SOCIAIS.map((r) => (
            <div className="input-group" key={r.key}>
              <label className="input-label">{r.label}</label>
              <input
                className="input"
                value={dados[r.key] || ""}
                onChange={set(r.key)}
                placeholder={r.placeholder}
                maxLength={200}
              />
            </div>
          ))}
        </div>

        <div style={{ ...SECAO_STYLE, marginTop: 6 }}>PAGAMENTO DE COMISSÃO</div>
        <div className="row-2col" style={{ gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Banco</label>
            <input className="input" value={dados.banco} onChange={set("banco")} placeholder="Itaú · 341" />
          </div>
          <div className="input-group">
            <label className="input-label">Chave PIX</label>
            <input className="input mono" value={dados.chave_pix} onChange={set("chave_pix")} />
          </div>
        </div>
        <div className="input-group">
          <label className="input-label">Ciclo de pagamento</label>
          <select className="input" value={dados.ciclo_pagamento} onChange={set("ciclo_pagamento")}>
            {CICLOS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div className="input-group">
          <label className="input-label">Observações</label>
          <textarea className="input" rows={3} value={dados.observacoes} onChange={set("observacoes")} />
        </div>

        {/* M-CRM-9: o campo existia no modelo e na API, mas nenhuma tela o
            oferecia — parceiro que parou de trabalhar com a marcenaria
            continuava aparecendo em toda lista de seleção, para sempre.
            Só na edição: parceiro nasce ativo. */}
        {editandoId && (
          <div className="input-group">
            <label className="input-label" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={dados.ativo}
                onChange={(e) => setDados((d) => ({ ...d, ativo: e.target.checked }))}
              />
              Parceiro ativo
            </label>
            <span className="input-help">
              Desmarque para aposentar o parceiro. Ele sai das listas de seleção,
              mas o histórico de indicações e comissões continua intacto.
            </span>
          </div>
        )}

        {erroForm && <div className="so-banner danger" role="alert">{erroForm}</div>}
      </form>
    </SlideOverPanel>
  );
}
